/**
 * Derivations the User Journey page performs over the payroll-connection
 * summary.
 *
 * Deliberately not in `common/payrollConnections/`: neither of these is part
 * of the shared panel's vocabulary. `resolveSyncStatusConnectionKey` reads a
 * `sync-status` report, a concept the panel has never heard of, and the header
 * line `resolveLastSuccessfulConnection` feeds is this page's alone. The
 * shared module keeps only what a second consumer of the panel would also
 * need.
 */
import {
    UNASSIGNED_CONNECTION_KEY,
    getConnectionPlatformLabel,
} from '../../common/payrollConnections/payrollConnections.constants';

/**
 * The member's most recent successful payroll connection across every
 * connection they have, for the at-a-glance line at the top of the page.
 *
 * Returns `null` when no connection has ever completed — the caller renders
 * "Never connected" rather than a placeholder date.
 *
 * @param {Array<Object> | null | undefined} connections
 * @returns {{ at: string, platformLabel: string, connectionKey: string } | null}
 */
export function resolveLastSuccessfulConnection(connections) {
    if (!Array.isArray(connections)) {
        return null;
    }

    let best = null;
    for (const connection of connections) {
        const at = connection?.last_successful_at;
        if (!at) continue;
        // String compare would be wrong across differing offsets (EST vs EDT
        // rows sit either side of a DST change), so compare instants.
        const instant = new Date(at).getTime();
        if (Number.isNaN(instant)) continue;
        if (!best || instant > best.instant) {
            best = {
                instant,
                at,
                platformLabel: getConnectionPlatformLabel(connection),
                connectionKey: connection?.connection_key ?? UNASSIGNED_CONNECTION_KEY,
            };
        }
    }

    if (!best) {
        return null;
    }
    return { at: best.at, platformLabel: best.platformLabel, connectionKey: best.connectionKey };
}

/**
 * The connection a `sync-status` report belongs to. That endpoint reports only
 * the latest non-DD-switch sync and carries its `account_id`, so the dimension
 * card belongs to exactly one panel — the others show no dimension pills.
 *
 * @param {{ account_id?: string | null } | null | undefined} report
 * @returns {string | null}
 */
export function resolveSyncStatusConnectionKey(report) {
    if (!report) {
        return null;
    }
    return report.account_id ?? UNASSIGNED_CONNECTION_KEY;
}
